import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Flex } from "../../../../../components/Flex";
import { Drawer } from "../../../../../components/Drawer";
import { Spacing } from "../../../../../theme/Spacing";
import { Text } from "../../../../../components/Text";
import { Tooltip, Switch } from "../../../../../utils/local-antd";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { PermissionAction } from "../../../../../model/permissions/types/PermissionAction";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import { permissionSliceSelectors } from "../../../../../redux/permission/permission.selector";
import { sortPermissions } from "../../../../permissions/utils/permissionUtils";
import { Button } from "../../../../../components/Button";
import { ModuleDefinition } from "../../../../../model/module/types/module-definition/ModuleDefinition";
import { faUserCircle } from "@fortawesome/free-solid-svg-icons/faUserCircle";
import { truncateString } from "../../../../../utils/String";
import { Lengths } from "../../../../../model/shared/enum/Lengths.enum";
import { useForm } from "react-hook-form";
import { ModuleDefinitionSetPermissionsDto } from "../../../../../model/module/dto/module-definition/ModuleDefinitionSetPermissionsDto";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";
import { AtiraToast } from "../../../../../utils/AtiraToast";
import { moduleActions } from "../../../../../redux/module/module.slice";
import { permissionActions } from "../../../../../redux/permission/permission.slice";
import { ImageCreateEdit } from "../../../../../components/ImageCreateEdit";
import { Info } from "../../../../../components/Info";
import { TestIDs } from "../../../../../tests/types/testIds";

const Title = styled(Text)<{ horizontal?: boolean }>`
  font-weight: bold;
  flex: 1;
  ${({ horizontal }) =>
    horizontal
      ? `
    padding-inline-end: ${Spacing.s}
  `
      : ""}
`;

const TableHeader = styled(Flex)`
  justify-content: space-between;
  padding: ${Spacing.s};
  border-bottom: ${({ theme }) => `2px solid ${theme.darkerSub}`};
`;

const TableRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.m} ${Spacing.s};
  border-bottom: ${({ theme }) => `1px solid ${theme.darkSub}`};
`;

const MemberImageWrapper = styled(Flex)`
  width: 2rem;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  overflow: hidden;

  .ant-image,
  svg {
    height: 100% !important;
  }

  img {
    height: 100% !important;
    object-fit: cover;
  }
`;

type ModuleDefinitionSettingsDrawerProps = {
  open: boolean;
  onClose: VoidFunction;
  moduleDefinition?: ModuleDefinition | null;
};

export const ModuleDefinitionSettingsDrawer: React.FC<
  ModuleDefinitionSettingsDrawerProps
> = ({ open, onClose, moduleDefinition }) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { reset, watch, setValue, getValues } =
    useForm<ModuleDefinitionSetPermissionsDto>();

  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const assignees = watch("assignees") || [];

  const handleSwitch = (
    memberId: string,
    action: Exclude<PermissionAction, PermissionAction.READ>,
    value: boolean,
  ) => {
    const updated = assignees.map((a) => {
      if (a.userId !== memberId) {
        return a;
      }

      return {
        userId: a.userId,
        permissions: { ...a.permissions, [action]: value },
      };
    });

    setValue("assignees", updated, { shouldDirty: true });
  };

  const onSave = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      await dispatch(
        moduleActions.manageModuleDefinitionPermissions(dto),
      ).unwrap();
      await Promise.all([
        dispatch(permissionActions.getMembers({ userId })).unwrap(),
        dispatch(moduleActions.getAllModuleDefinitions({ userId })).unwrap(),
      ]);

      onClose();

      AtiraToast.success(
        t("module.settings.update.success", {
          name: moduleDefinition?.name,
        }),
      );
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (moduleDefinition && open) {
      reset({
        assignees: members.map((m) => ({
          userId: m._id,
          permissions: m.modulePermissions?.find(
            (mp) => mp.moduleDefinitionId === moduleDefinition._id,
          )?.permissions || {
            [PermissionAction.CREATE]: false,
            [PermissionAction.UPDATE]: false,
            [PermissionAction.DELETE]: false,
          },
        })),
        moduleDefinitionId: moduleDefinition._id,
        userId,
      });
    }
  }, [moduleDefinition, reset, open, members, userId]);

  if (!moduleDefinition) {
    return null;
  }

  return (
    <Drawer
      title={t("module.settings.title", { name: moduleDefinition.name })}
      open={open}
      onClose={onClose}
      size={"large"}
    >
      <Flex flexDirection="column" width="100%" gap="m">
        <Text fontWeight={"bold"} fontSize="l" color="main">
          {t("common.permissions")}
        </Text>
        <Info
          info={t("module.settings.info", { name: moduleDefinition.name })}
        />

        <TableHeader>
          <Flex flex={1} />

          <Title>{t("common.create")}</Title>
          <Title>{t("common.update")}</Title>
          <Title>{t("common.delete")}</Title>
        </TableHeader>

        {members.map((member) => {
          const assignee = assignees.find((a) => a.userId === member._id);

          const value = assignee?.permissions || {
            CREATE: false,
            UPDATE: false,
            DELETE: false,
          };

          return (
            <TableRow key={member._id}>
              <Flex flex={1}>
                <Tooltip title={member?.name}>
                  <Flex alignItems="center" gap="s">
                    <MemberImageWrapper>
                      {member?.picture ? (
                        <ImageCreateEdit src={member.picture} />
                      ) : (
                        <AtiraIcon icon={faUserCircle} />
                      )}
                    </MemberImageWrapper>

                    <Text fontWeight="bold" fontSize="s">
                      {truncateString(member?.name || "", Lengths.NAME)}
                    </Text>
                  </Flex>
                </Tooltip>
              </Flex>

              {Object.entries(
                sortPermissions({
                  CREATE: value.CREATE || false,
                  UPDATE: value.UPDATE || false,
                  DELETE: value.DELETE || false,
                }),
              ).map(([action, checked]) => {
                return (
                  <Flex flex={1} key={action}>
                    <Switch
                      checked={checked}
                      data-testid={TestIDs.modules.permissions.switch(
                        member._id,
                        action,
                      )}
                      onChange={(isChecked) =>
                        handleSwitch(
                          member._id,
                          action as Exclude<
                            PermissionAction,
                            PermissionAction.READ
                          >,
                          isChecked,
                        )
                      }
                    />
                  </Flex>
                );
              })}
            </TableRow>
          );
        })}

        <Button
          loading={loading}
          onClick={onSave}
          margin="none"
          marginTop="m"
          width="fit-content"
          dataTestId={TestIDs.modules.permissions.save}
        >
          {t("common.update")}
        </Button>
      </Flex>
    </Drawer>
  );
};
